import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { PARTNER_ENTITLEMENTS, PartnerEntitlement, Skeleton, withStyles } from 'vopay';
import { BodyText, Card, SwitchIndicator, Title } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useFetchPartnerPartnerEntitlements from '@/hooks/useApi/useFetchPartnerPartnerEntitlements';
import styles from './PartnerEntitlementsTab.module.scss';

const cn = withStyles(styles);

type PartnerEntitlementsTabProps = {
  partnerAccountID: string;
};

/** The note's "contact Support Team" link: Figma Body/16/Medium in the link blue, inline in the sentence. */
function SupportLink({ children }: { children?: ReactNode }) {
  return (
    <Link to="/tickets/create" className={cn('supportLink')}>
      <BodyText as="span" weight="md" className={cn('supportLinkText')}>
        {children}
      </BodyText>
    </Link>
  );
}

/**
 * The partner page's Partner Entitlement tab (spec 5.6.3): what the partner account may do on the
 * platform, as read-only switches — the ten flags `create-partner` copied from this partner's own
 * profile, which only Support can change. Distinct from the service permissions the header card's
 * View Permissions opens (spec 5.7): those are payment rails, these are platform capabilities.
 */
export default function PartnerEntitlementsTab({ partnerAccountID }: PartnerEntitlementsTabProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerEntitlementsTab' });
  const toast = useToast();
  const { partnerEntitlements, fetchPartnerPartnerEntitlements, reset } = useFetchPartnerPartnerEntitlements();
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    reset();
    setHasFailed(false);

    // The hook drops a superseded response itself, but its rejection still reaches this caller — so a
    // request for the partner account the user has since left must not mark this one failed.
    let isCurrentPartner = true;

    fetchPartnerPartnerEntitlements(partnerAccountID).catch((error) => {
      if (!isCurrentPartner) return;

      // The hook has logged the cause; the tab says the flags are unavailable and the toast says why.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasFailed(true);

      toast.neutral(
        t('loadError', { defaultValue: 'Could not load the partner entitlements: {{errorMessage}}', errorMessage }),
      );
    });

    return () => {
      isCurrentPartner = false;
    };
    // toast is deliberately left out: the provider recreates its value every render, so depending
    // on it would refetch on every toast.
  }, [partnerAccountID, fetchPartnerPartnerEntitlements, reset, t]);

  // Keyed on the flag, which stays the logic key; only the rendered string is localized. A flag the
  // backend adds before this list does is not shown, by design: the spec names exactly these ten.
  const labels: Record<PartnerEntitlement, string> = {
    AccountManagementEnabled: t('Labels.accountManagementEnabled', { defaultValue: 'Account Management' }),
    AccountOnboardingFormAccess: t('Labels.accountOnboardingFormAccess', { defaultValue: 'Onboarding Form Access' }),
    AccountEndpointAccess: t('Labels.accountEndpointAccess', { defaultValue: 'API Endpoint Access' }),
    AccountCredentialsAccess: t('Labels.accountCredentialsAccess', { defaultValue: 'API Credentials Access' }),
    AccountTransfersEnabled: t('Labels.accountTransfersEnabled', { defaultValue: 'Account Transfers' }),
    SetAccountPermissionsEnabled: t('Labels.setAccountPermissionsEnabled', { defaultValue: 'Set Account Permissions' }),
    PartnerBillsAccount: t('Labels.partnerBillsAccount', { defaultValue: 'Bills Its Own Accounts' }),
    PartnerEmailsAccount: t('Labels.partnerEmailsAccount', { defaultValue: 'Emails Its Own Accounts' }),
    CanAddDomesticAccounts: t('Labels.canAddDomesticAccounts', {
      defaultValue: 'Can Add Domestic Accounts (same country as this partner)',
    }),
    CanAddForeignAccounts: t('Labels.canAddForeignAccounts', {
      defaultValue: 'Can Add Foreign Accounts (any other country)',
    }),
  };

  const title = t('title', { defaultValue: 'Platform Capabilities' });

  return (
    <section className={cn('tab')} aria-label={title} data-testid="partnerEntitlementsTab">
      <div className={cn('intro')}>
        {/* Figma: H4/Large Title 20/Emphasized over Body/16/Regular. */}
        <Title level="4" className={cn('title')}>
          {title}
        </Title>

        <BodyText>
          <Trans
            t={t}
            i18nKey="note"
            defaults="These settings were inherited from your profile when the partner account was created. To change them, <supportLink>contact Support Team</supportLink>."
            components={{ supportLink: <SupportLink /> }}
          />
        </BodyText>
      </div>

      {!partnerEntitlements && !hasFailed && <Skeleton height={600} data-testid="partnerEntitlementsSkeleton" />}

      {hasFailed && (
        <BodyText className={cn('message')}>
          {t('unavailable', { defaultValue: 'The partner entitlements are unavailable.' })}
        </BodyText>
      )}

      {partnerEntitlements && (
        <Card variant="section" className={cn('card')}>
          {PARTNER_ENTITLEMENTS.map((entitlement) => (
            // SwitchIndicator is visual only; as its docs describe, the checkbox after it carries the
            // state, here disabled because the flags are read-only. The label names it.
            <label key={entitlement} className={cn('row')}>
              {/* Figma: Body/16/Medium beside a disabled Switch. */}
              <BodyText weight="md">{labels[entitlement]}</BodyText>
              <SwitchIndicator checked={partnerEntitlements[entitlement]} readOnly />

              <input
                className={cn('hiddenCheckbox')}
                type="checkbox"
                name={entitlement}
                checked={partnerEntitlements[entitlement]}
                disabled
                readOnly
              />
            </label>
          ))}
        </Card>
      )}
    </section>
  );
}
